/**
 * Android closed-test testers (owner request 2026-10-25): Google Play publishes a new personal developer's
 * app only after a closed test with 12 testers for 14 days. Testers sign up on a public page with their
 * Google account email and tick their own steps; the administration copies the emails into Play.
 */
import { z } from "zod";
import { phone } from "./fields.js";
export const ANDROID_TESTERS_NEEDED = 12;
/**
 * The test days after the last step (owner decision 2026-10-25): Google's 14 days and 3 to spare. Every 5 days
 * (days 5, 10, 15) a WhatsApp reminder asks the tester to open the app and confirm it with «فتحت».
 */
export const ANDROID_TEST_DAYS = 17;
export const ANDROID_TEST_REMIND_EVERY = 5;
export const AndroidTesterStep = z.enum(["joined", "installed", "tried"]);
/** The header the tester's page sends with the token it keeps. */
export const TESTER_TOKEN_HEADER = "x-tester-token";
export const RegisterAndroidTesterBody = z.object({
    organizationId: z.uuid(),
    name: z.string().trim().min(2, "اكتب اسمك").max(80),
    email: z.string().trim().toLowerCase().pipe(z.email("البريد الإلكتروني غير صحيح").max(120)),
    /** WhatsApp number for the messages at each step. */
    phone,
    /** Honeypot: humans never fill it. */
    website: z.string().max(200).optional(),
});
export const RegisterAndroidTesterResponse = z.object({ token: z.string() });
export const AndroidTesterMeResponse = z.object({
    name: z.string(),
    email: z.string(),
    /** The administration added the email to Google Play's tester list. */
    addedAt: z.string().nullable(),
    joinedAt: z.string().nullable(),
    installedAt: z.string().nullable(),
    triedAt: z.string().nullable(),
    joinUrl: z.string().nullable(),
    demoPhone: z.string().nullable(),
    demoPassword: z.string().nullable(),
    /** Day 1 is the day the last step was done; null before it. */
    testDay: z.number().int().nullable(),
    openDays: z.number().int(),
    openedToday: z.boolean(),
});
export const AndroidTesterStepBody = z.object({ step: AndroidTesterStep });
export const AndroidTesterOpenedResponse = z.object({ counted: z.boolean() });
export const AndroidTester = z.object({
    id: z.uuid(),
    name: z.string(),
    email: z.string(),
    phone: z.string().nullable(),
    addedAt: z.string().nullable(),
    joinedAt: z.string().nullable(),
    installedAt: z.string().nullable(),
    triedAt: z.string().nullable(),
    createdAt: z.string(),
    testDay: z.number().int().nullable(),
    /** Days they ticked «فتحت». */
    openDays: z.number().int(),
    /** Days since their last «فتحت» (or since the last step when none); null before the last step. */
    idleDays: z.number().int().nullable(),
});
const TestSettingsFields = {
    joinUrl: z
        .url({ protocol: /^https$/, error: "رابط الانضمام يبدأ بـ https://" })
        .max(300)
        .nullable(),
    demoPhone: z.string().trim().max(30).nullable(),
    demoPassword: z.string().max(100).nullable(),
    /** The testers' own WhatsApp gateway (UltraMsg-compatible), separate from the organization's. */
    waBaseUrl: z
        .url({ protocol: /^https?$/ })
        .max(300)
        .nullable(),
    waInstanceId: z.string().trim().max(120).nullable(),
    waActive: z.boolean(),
};
export const AndroidTestSettings = z.object({
    ...TestSettingsFields,
    /** The token is never sent back; only whether one is stored. */
    hasWaToken: z.boolean(),
});
export const AndroidTestersQuery = z.object({ organizationId: z.uuid() });
export const AndroidTestersResponse = z.object({
    testers: z.array(AndroidTester),
    settings: AndroidTestSettings,
});
export const UpdateAndroidTestSettingsBody = z.object({
    ...TestSettingsFields,
    organizationId: z.uuid(),
    /** Omitted: keep the stored token. */
    waToken: z.string().trim().min(1).max(500).optional(),
});
export const TestAndroidWhatsAppBody = z.object({ organizationId: z.uuid(), phone });
/** What a WhatsApp send reports back to the portal: whether the gateway accepted it, or its error text. */
export const AndroidTesterMessageResponse = z.object({ sent: z.boolean(), error: z.string().nullable() });
//# sourceMappingURL=android-testers.js.map